import { useEffect, useState } from "react";

import { fetchHomeContent } from "plg/api/plgHome";
import type { PlgHomeContent } from "plg/api/plgHome";
import { getPlgProfile } from "plg/auth/plgTokenStore";
import type { TalentListItem } from "components/Screens/Marketplace/Customer/types";

/** How long the three preview profiles are reused before Home asks again. */
export const PREVIEW_TTL_MS = 5 * 60 * 1000;

/**
 * Home's two requests, kept in memory for this tab so going back to Home does not ask again (product 2026-10-26):
 * - the page copy (PLG_HOME_CONFIG) is the same for everyone: asked once;
 * - the preview profiles are redacted for who is looking: kept per customer and verification state, for a few
 *   minutes. Only in memory, never in storage, so no talent data outlives the tab; the server still redacts.
 * A failed request is not kept, so the next visit tries again.
 */
let content: Promise<PlgHomeContent> | null = null;
let preview: { key: string; at: number; people: Promise<TalentListItem[]> } | null = null;

export function resetPlgHomeCache() {
  content = null;
  preview = null;
}

function loadContent(): Promise<PlgHomeContent> {
  if (!content) {
    content = fetchHomeContent();
    content.catch(() => {
      content = null;
    });
  }
  return content;
}

function loadPreview(fetchPeople: () => Promise<TalentListItem[]>): Promise<TalentListItem[]> {
  const profile = getPlgProfile();
  const key = `${profile?.plgCustomerId ?? ""}:${profile?.isVerified ? "verified" : "unverified"}`;
  if (!preview || preview.key !== key || Date.now() - preview.at > PREVIEW_TTL_MS) {
    const people = fetchPeople();
    const entry = { key, at: Date.now(), people };
    preview = entry;
    people.catch(() => {
      if (preview === entry) preview = null;
    });
  }
  return preview.people;
}

export default function usePlgHomeData(fetchPeople: () => Promise<TalentListItem[]>) {
  const [homeContent, setHomeContent] = useState<PlgHomeContent | null>(null);
  const [people, setPeople] = useState<TalentListItem[]>([]);

  useEffect(() => {
    let live = true;
    loadContent()
      .then((c) => live && setHomeContent(c))
      .catch(() => live && setHomeContent(null));
    loadPreview(fetchPeople)
      .then((p) => live && setPeople(p))
      .catch(() => live && setPeople([]));
    return () => {
      live = false;
    };
  }, []);

  return { content: homeContent, people };
}
